import { RunState, RunStateToColor, RunStateToName } from "../../constants/workflow";
import { TableBodyRow, TableData, TableHeader, TableHeaderRow } from "../Common/TableElements";
import { NanonetsIcon } from "../../svgUrls";
import StatusChip from "../Common/StatusChip";
import { Run } from "../../types/workflow";
import { MoreVert } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import clsx from "clsx";
import { formatDateTime } from "../../utils/misc";

type WorkflowHistoryTableProps = {
    workflowHistory: Run[];
    selectedRow: null | Run;
    onHistoryRowClick: (run: Run) => void;
    setMenuAnchorEl: (anchor: HTMLElement | null) => void;
    menuOpenedForId: string;
    setMenuOpenedForId: (id: string) => void;
}

const WorkflowHistoryTable = ({
    workflowHistory,
    selectedRow,
    onHistoryRowClick,
    setMenuAnchorEl,
    menuOpenedForId,
    setMenuOpenedForId,
}: WorkflowHistoryTableProps) => {
    return (
        <div className="overflow-auto h-full">
            <table className="w-full border-spacing-0 border-separate">
                <thead className="sticky top-0 z-10">
                    <TableHeaderRow>
                        <TableHeader additionalClasses="text-left pl-6">Timestamp</TableHeader>
                        <TableHeader additionalClasses="text-left">Run #</TableHeader>
                        <TableHeader additionalClasses="text-left">Status</TableHeader>
                        <TableHeader additionalClasses="text-left">Last run step</TableHeader>
                        <TableHeader additionalClasses="text-center sticky right-0 bg-inherit">{""}</TableHeader>
                    </TableHeaderRow>
                </thead>
                <tbody>
                    {workflowHistory?.length && Array.isArray(workflowHistory) ?
                        workflowHistory?.map((element) => {
                            return (
                                <TableBodyRow 
                                    additionalClasses={clsx(
                                        "hover:cursor-pointer hover:bg-backgroundBlueLight transition-colors",
                                        { "bg-backgroundBlue": selectedRow?.id === element.id }
                                    )}
                                    key={element.id}
                                    isSelected={selectedRow?.id === element.id || menuOpenedForId === element.id}
                                    onClick={() => onHistoryRowClick(element)}
                                >
                                    <TableData additionalClasses="text-left pl-6 text-xs text-greyishBlue">
                                        {formatDateTime(element.created_time)}
                                    </TableData>
                                    <TableData additionalClasses="text-left font-mono text-xs text-greyishBlue">
                                        {element.id}
                                    </TableData>
                                    <TableData>
                                        {element.state === RunState.RunPending ? (
                                            <div className="w-fit rounded p-1 text-xs bg-[#fff5e9] text-[#DF8518] border border-solid border-[#f9dfbf] font-medium flex items-center gap-1.5">
                                                <CircularProgress size={12} thickness={4} className="!text-[#DF8518]" />
                                                <span>Pending</span>
                                            </div>
                                        ) : (
                                            <StatusChip
                                                statusText={RunStateToName[element.state]}
                                                themeColor={RunStateToColor[element.state]}
                                                showBorder
                                                additionalClasses="font-medium"
                                            />
                                        )}
                                    </TableData>
                                    <TableData>
                                        <div className="flex gap-2 items-center">
                                            <NanonetsIcon />
                                            <span className="text-sm text-greyishBlueMid">
                                                {element.last_run_node_run_instance?.node?.name || 'N/A'}
                                            </span>
                                        </div>
                                    </TableData>
                                    <TableData additionalClasses={clsx(
                                        "!w-7 sticky right-0 bg-inherit text-center",
                                        { "border-r-2 border-solid border-nanonetsBlue": selectedRow?.id === element.id }
                                    )}>
                                        <div 
                                            className="p-1.5 rounded-md hover:bg-backgroundGrey cursor-pointer transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMenuAnchorEl(e.currentTarget);
                                                setMenuOpenedForId(element.id);
                                            }}
                                        >
                                            <MoreVert className="!text-base text-lightSlateGrey" />
                                        </div>
                                    </TableData>
                                </TableBodyRow>
                            )
                        })
                        : <TableBodyRow>
                            <TableData colSpan={5} additionalClasses="text-center py-8">
                                <div className="text-greyishBlue">No history to display</div>
                            </TableData>
                        </TableBodyRow>
                    }
                </tbody>
            </table>
        </div>       
    )
}

export default WorkflowHistoryTable;